import {
  AfterViewChecked,
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppInfoService } from 'core/app-info/app-info.service';
import { countriesFromEurope, countryCodeValues } from 'core/app-info/country-specifics.enum';
import { AppStateService } from 'core/app-state/app-state.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { SignalRService } from 'core/signalR-service/signal-r.service';
import { CanComponentDeactivate } from 'core/unsaved-changes/unsaved-changes-guard.class';
import { PriceAsService } from 'src/app/modules/marketing/products/services/price-as-service/price-as.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BaseResponse } from 'src/app/modules/shared/entities/base-response';
import { QuoteDetails } from 'src/app/modules/shared/entities/quote-details.class';
import { SignalRUpload } from 'src/app/modules/shared/entities/signalr-upload.class';
import { RestoredQuote } from 'src/app/modules/shared/entities/restored-quote.class';
import { QuoteEditProgress } from 'src/app/modules/shared/entities/quote-edit-progress.class';
import { ImportItemsQuoteService } from 'src/app/modules/shared/import-quotes-and-orders/import-items-quote.service';
import { Customer } from '../../../shared/entities/customer.class';
import { SignalrConnectionType } from '../../../shared/entities/signalrConnectionType.enum';
import { SignalrMethodName } from '../../../shared/entities/signalrMethodName.enum';
import { SignalrNotification } from '../../../shared/entities/SignalrNotification.class';
import { SignalrProgressState } from '../../../shared/entities/signalrProgressState.enum';
import { Icon } from '../../../shared/icon/icon.enum';
import { PaymentSummary } from '../entities/payment-summary.class';
import { QuoteSection, QuoteStep } from '../entities/quote-step.enum';
import { ShortContact } from '../entities/short-contact.class';
import { PaymentDetailsComponent } from '../payment-details/payment-details.component';
import { ProductGridComponent } from '../product-grid/product-grid.component';
import { QuoteDetailsComponent } from '../quote-details/quote-details.component';
import { QuoteExpiredComponent } from '../quote-expired/quote-expired.component';
import { QuoteVersioningComponent } from '../quote-versioning/quote-versioning.component';
import { PaymentDetailsService } from '../services/payment-details.service';
import { QuoteDetailsService } from '../services/quote-details.service';
import { QuoteToolsService } from '../services/quote-tools.service';
import { BarclaysPaymentService } from '../services/barclays-payment-service';
import { BarclaysRequest, BarclaysResponse } from '../entities/barclays-request.class';
import { PaymentStatus } from '../entities/paymentStatuses';
import { PaymentBarclaysLegacyComponent } from '../payment-barclays-legacy/payment-barclays-legacy.component';
import { QuoteEmailNotificationComponent } from '../quote-email-notification/quote-email-notification.component';
import { PaymentDetails } from '../entities/payment-details.class';
import { QuoteOptionsComponent } from '../quote-options/quote-options.component';
import { PrintExportQuoteComponent } from './print-export-quote/print-export-quote.component';

@Component({
  selector: 'app-quotes',
  templateUrl: './quote.component.html',
  styleUrls: ['./quote.component.scss'],
  standalone: false
})

export class QuoteComponent implements OnInit, AfterViewInit, OnDestroy, CanComponentDeactivate, AfterViewChecked {
  appStrings: AppLocalization;
  isMobile: boolean;
  dialogButtons: any;
  dialogTitle: string;
  dialogText: any;
  dialogOpen$ = new EventEmitter<boolean>();
  icon = Icon;
  stepsCompletion = {};
  quoteNumber: string;
  productCount: number;
  errorCount: number;
  customer: Customer;
  currencySign: string;
  weightSymbol: string;
  summary: any = {};
  lead: any;
  canEdit: boolean;
  quoteIsCompleted: boolean;
  isLead: boolean;
  isAccountManagerTypeUser: boolean;
  noProducts: boolean;
  called = false;
  paymentSummary = new PaymentSummary();
  // this is for the main overlay
  isLoading = false;
  doNotRefresh = false; // determines if do not refresh prompts are shown
  // this is for the buttons
  quoteSaveInProgress = false;
  orderValidationInProgress = false;
  orderIsCompleted = false;
  orderConfirmationNumber = '';
  orderEnsEmails: string[] = [];
  orderContacts: ShortContact[] = [];
  quoteId = ''; // the orderId after the quote has been completed
  warnForErrors = false;
  quoteCompletionDialogWidth = '370px';
  quoteRestoredMessage;
  restoredQuote: RestoredQuote;
  expiredQuote: any;
  isExpiredQuote: boolean;
  migratedQuote: any;
  isMigratedQuote: boolean;
  quoteExpiredDialogWidth: '370px';
  unloadListener: any;
  quoteRevisionId = ''; // quote revision id after the order has been completed
  startItemEditInProgress: boolean;
  private saveAndCloseQuoteTriggered = false;
  itemizeInProgress = false;
  sectionErrors = {
    products: false,
    notifications: false,
    payment: false,
    options: false
  };
  toolEditInProgress = false;
  isOnline: boolean;
  paymentSummaryUpdated = true;
  productListHasChanges;
  hasTaxComputingError = false;
  electronAppOutOfSync = false;
  creditCardStartDelayms = 1000;
  taxNaming: any;
  customerCountryCode: string;
  priceMatrixChangedAfterMerge: boolean;
  visitedLink: boolean;
  quoteToUpdate: any;
  shouldRefreshPage = false;
  accountManagerId = '';
  atpRefreshing = false;
  createdFromPriceAs = false;
  hideInactiveCustomers = true;
  highValueQuoteLimit = 50000;
  importQuoteActive = false;

  @ViewChild('quoteDetailsPanel') private quoteDetailsPanelDesktop: MatExpansionPanel;
  @ViewChild('quoteProductsPanel') private quoteProductsPanelDesktop: MatExpansionPanel;
  @ViewChild('quotePaymentPanel') private quotePaymentPanelDesktop: MatExpansionPanel;
  @ViewChild('quoteNotificationPanel') private quoteNotificationPanelDesktop: MatExpansionPanel;
  @ViewChild('quoteOptionsPanel') private quoteOptionsPanelDesktop: MatExpansionPanel;
  @ViewChild('quoteHistoryPanel') private quoteHistoryPanelDesktop: MatExpansionPanel;

  @ViewChild('quoteDetailsPanelMobile') quoteDetailsPanelMobile: MatExpansionPanel;
  @ViewChild('quoteProductsPanelMobile') quoteProductsPanelMobile: MatExpansionPanel;
  @ViewChild('quotePaymentPanelMobile') quotePaymentPanelMobile: MatExpansionPanel;
  @ViewChild('quoteNotificationPanelMobile') quoteNotificationPanelMobile: MatExpansionPanel;
  @ViewChild('quoteOptionsPanelMobile') quoteOptionsPanelMobile: MatExpansionPanel;

  @ViewChild(QuoteDetailsComponent) quoteDetailsComp: QuoteDetailsComponent;
  @ViewChild(ProductGridComponent) quoteProductsComp: ProductGridComponent;
  @ViewChild(PaymentDetailsComponent) quotePaymentComp: PaymentDetailsComponent;
  @ViewChild(QuoteOptionsComponent) quoteOptionsComp: QuoteOptionsComponent;
  @ViewChild(QuoteVersioningComponent) quoteVersioningComp: QuoteVersioningComponent;
  @ViewChild(QuoteEmailNotificationComponent) quoteNotificationComp: QuoteEmailNotificationComponent;

  get quoteDetailsPanel(): MatExpansionPanel {
    return this.isMobile ? this.quoteDetailsPanelMobile : this.quoteDetailsPanelDesktop;
  }

  get quoteProductsPanel(): MatExpansionPanel {
    return this.isMobile ? this.quoteProductsPanelMobile : this.quoteProductsPanelDesktop;
  }

  get quotePaymentPanel(): MatExpansionPanel {
    return this.isMobile ? this.quotePaymentPanelMobile : this.quotePaymentPanelDesktop;
  }

  get quoteNotificationPanel(): MatExpansionPanel {
    return this.isMobile ? this.quoteNotificationPanelMobile : this.quoteNotificationPanelDesktop;
  }

  get quoteOptionsPanel(): MatExpansionPanel {
    return this.isMobile ? this.quoteOptionsPanelMobile : this.quoteOptionsPanelDesktop;
  }

  get quoteHistoryPanel(): MatExpansionPanel {
    return this.quoteHistoryPanelDesktop;
  }

  get versioningEnsEmails(): string[] {
    return this.getEnsEmailsFromNotifications();
  }

  get versioningContacts(): ShortContact[] {
    return this.quoteNotificationComp?.contactsList ?? [];
  }

  private currentPanel: MatExpansionPanel;
  private subscriptions = new Subscription();
  private routeSubscription = new Subscription();
  private quoteCreatedFromQuotes;
  private paymentDetailsResponse: PaymentDetails;
  private actionAfterQuoteSave;
  updatingPriceMatrixAfterMergeButton: boolean;
  taxForLead: string;

  constructor(
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private quoteDetailsService: QuoteDetailsService,
    private router: Router,
    private route: ActivatedRoute,
    private paymentDetailsService: PaymentDetailsService,
    private notificationService: NotificationService,
    private quoteToolsService: QuoteToolsService,
    private barclaysPaymentService: BarclaysPaymentService,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
    private signalrService: SignalRService,
    private app: ApplicationRef,
    private priceAsService: PriceAsService,
    private deviceService: DeviceDetectorService,
    private importItemsQuoteService: ImportItemsQuoteService
  ) {
    this.stepsCompletion[QuoteStep.QuoteDetails] = false;
    this.stepsCompletion[QuoteStep.ProductList] = false;
    this.stepsCompletion[QuoteStep.PaymentDetails] = false;
  }

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.initData();
    this.routeSubscription.add(
      this.router.events.pipe(filter((evt: any) => evt instanceof NavigationStart))
        .subscribe(event => {
          // reset quote restored message when:
          // back button pressed
          // switches between quotes (import)
          if (event.navigationTrigger === 'popstate' ||
            event.navigationTrigger === 'imperative') {
            this.resetQuoteRestored();
          }
        })
    );
    this.routeSubscription.add(
      this.route.params.subscribe(() => this.initDataForQuote())
    );

    this.unloadListener = event => {
      if (this.doNotRefresh) {
        event.preventDefault();
        event.returnValue = this.appStrings.payment_pleaseDoNotRefresh;
        return this.appStrings.payment_pleaseDoNotRefresh;
      }
    };
    window.addEventListener('beforeunload', this.unloadListener);
  }

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
  }

  ngAfterViewChecked(): void {
    if (this.quoteDetailsPanel && !this.currentPanel) {
      this.currentPanel = this.quoteDetailsPanel;
      this.currentPanel.open();
      this.cdRef.detectChanges();
    }
  }

  private initData() {
    this.quoteNumber = '';
    this.paymentDetailsService.resetCurrentFreightState();
    this.isOnline = this.appStateService.state.isOnline;
    this.weightSymbol = this.appInfoService.weightSymbol;
    this.currencySign = this.appInfoService.currencySymbol;
    this.appStrings = this.appInfoService.appStrings;
    this.summary = this.quoteDetailsService.summary;
    this.paymentDetailsResponse = new PaymentDetails();
    this.isAccountManagerTypeUser = this.appInfoService.isAccountManager();
  }

  private initDataForQuote() {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    const leadId = this.route.snapshot.paramMap.get('leadId');
    let customerId = this.route.snapshot.paramMap.get('customerId');
    const quoteId = this.route.snapshot.paramMap.get('quoteId');
    this.createdFromPriceAs = Boolean(this.route.snapshot.data.fromPriceAs);
    this.handlePaymentRedirectState();
    if (this.route.snapshot.paramMap.get('nonce') && this.quoteVersioningComp) {
      this.quoteVersioningComp.getQuoteRevisions();
      this.goToQuoteDetails(null);
    }
    if (this.createdFromPriceAs && this.priceAsService.quoteInfo?.customer) {
      customerId = this.priceAsService.quoteInfo.customer.id;
    }
    this.noProducts = false;
    this.customerCountryCode = this.appInfoService.userData.UserInfo.CountryCode;
    this.determineTaxNaming(this.customerCountryCode);
    this.quoteCreatedFromQuotes = !leadId && !quoteId && !customerId;
    this.isLoading = !this.quoteCreatedFromQuotes;  // no loading overlay for new quotes at this time
    this.isExpiredQuote = false;
    this.isMigratedQuote = false;
    this.electronAppOutOfSync = this.appStateService.checkIfElectronAppIsOutOfSync();
    this.subscriptions.add(this.quoteDetailsService.quoteNumber$.subscribe(quoteNumber => this.quoteNumber = quoteNumber));
    this.subscriptions.add(this.quoteDetailsService.canEdit$.subscribe(canEdit => {
      if (this.electronAppOutOfSync) {
        this.canEdit = false;
        return;
      }
      this.canEdit = canEdit;
    }));

    this.subscriptions.add(this.quoteDetailsService.isCompleted$.subscribe(isCompleted => {
      this.quoteIsCompleted = isCompleted;
    }));

    this.subscriptions.add(this.quoteToolsService.itemizeInProgress$.subscribe(
      actionInProgress => this.itemizeInProgress = actionInProgress));

    this.subscriptions.add(this.quoteToolsService.toolEditInProgress$.subscribe(
      progress => this.toolEditInProgress = progress));

    this.subscriptions.add(this.quoteDetailsService.setCustomer$.subscribe(customer => {
      this.isLoading = false; // setting isLoading to false when the customer is loaded (this is the last detail in the quote header)
      this.updateCustomer(customer);
    }));
    this.subscriptions.add(this.quoteToolsService.productCount$.subscribe(productCount => this.productCount = productCount));
    this.subscriptions.add(this.quoteToolsService.errorCount$.subscribe(errorCount => {
      this.errorCount = errorCount;
      // Clear products error when error count becomes 0
      if (errorCount === 0) {
        this.clearSectionError(QuoteSection.Products);
      }
    }));
    this.subscriptions.add(this.paymentDetailsService.paymentSummary$.subscribe(paymentSummary =>
      this.paymentSummary = paymentSummary ?? new PaymentSummary()
    ));
    this.subscriptions.add(this.paymentDetailsService.paymentSummaryError$.subscribe(isWithError =>
      this.hasTaxComputingError = isWithError));
    this.subscriptions.add(this.quoteDetailsService.quoteRestored$.subscribe(quoteToBeRestored => {
      this.restoredQuote = quoteToBeRestored;
      this.quoteRestoredMessage = this.appStrings.quote_restoreViewMessage
        .replace('{0}', this.restoredQuote.quoteRevisionNumber);
      if (this.productListHasChanges) {
        this.resetQuoteRestored(true); // changes in products list on a restored quote (import products from other quote)
        this.subscriptions.add(this.quoteToolsService.paymentSummaryUpdated$.subscribe(value => this.paymentSummaryUpdated = value));
      }
    }));
    this.subscriptions.add(this.quoteToolsService.productListHasChanges$.subscribe(value => {
      this.productListHasChanges = value;
      if (value && this.quoteRestoredMessage) {
        this.resetQuoteRestored(true); // changes in products list
      }
      this.quoteToolsService.toolUpdateCallback();
    }));
    this.subscriptions.add(this.quoteToolsService.checkForResetQuoteRestored$.subscribe(doTheCheck => {
      if (doTheCheck && this.quoteRestoredMessage) {
        this.resetQuoteRestored(true); // changes in product list after items deletion
      }
    }));
    this.subscriptions.add(this.signalrService.validateOrderMessageReceived$
      .subscribe((validateOrderProgress: SignalRUpload) =>
        this.setOrderValidateProgress(validateOrderProgress))
    );
    this.subscriptions.add(this.signalrService.updatePricingProgressReceived$
      .subscribe((updatePricingProgress: SignalRUpload) =>
        this.setUpdatePricingProgress(updatePricingProgress))
    );
    this.subscriptions.add(this.quoteDetailsService.quoteIsSaving$.subscribe(value => this.quoteSaveInProgress = value));
    this.subscriptions.add(this.quoteToolsService.updateCWInProgress$.subscribe(updateProgress => this.isLoading = updateProgress));
    this.subscriptions.add(this.quoteToolsService.updateItemInProgress$
      .subscribe(progress => this.startItemEditInProgress = progress));
    this.subscriptions.add(this.signalrService.editQuoteProgressReceived$
      .subscribe((editQuoteProgress: QuoteEditProgress) => this.setEditQuoteProgress(editQuoteProgress)));
    this.subscriptions.add(this.quoteToolsService.paymentSummaryUpdated$.subscribe(value => this.paymentSummaryUpdated = value));
    this.subscriptions.add(this.paymentDetailsService.customerCountryCode$.subscribe(
      customerCountryCode => {
        this.customerCountryCode = customerCountryCode;
        this.determineTaxNaming(this.customerCountryCode);
      }));
    this.subscriptions.add(this.quoteDetailsService.currencySign$.subscribe(currencySign =>
      this.currencySign = currencySign
    ));
    this.subscriptions.add(this.quoteDetailsService.priceMatrixOrCustomerChangedNotification$
      .subscribe(() => this.displayNotificationBasedOnQuoteDetails()));
    this.subscriptions.add(this.quoteDetailsService.setAccountManager$.subscribe(amId => this.accountManagerId = amId));
    this.subscriptions.add(this.quoteToolsService.atpIsRefreshing$.subscribe(isRefreshing => this.atpRefreshing = isRefreshing));

    // Subscribe to destination quote changes
    this.subscriptions.add(this.importItemsQuoteService.getDestinationQuoteId$()
      .subscribe(destinationQuoteId => {
        this.importQuoteActive = !!destinationQuoteId && !this.router.url.endsWith('/new');
      }));

  }

  /**
   * Subscribe to form changes and clear section errors when forms become valid.
   * Called from ngAfterViewInit to ensure @ViewChild refs are available.
   */
  private subscribeToFormChanges() {
    if (this.quotePaymentComp?.paymentDetailsForm) {
      this.subscriptions.add(
        this.quotePaymentComp.paymentDetailsForm.valueChanges.subscribe(() => {
          if (this.sectionErrors.payment && this.quotePaymentComp?.paymentDetailsForm?.valid) {
            const isPaymentValid = this.quotePaymentComp.validatePayment();
            const isPOValid = this.quotePaymentComp.isPOAttachmentValid();
            if (isPaymentValid && isPOValid) {
              this.clearSectionError(QuoteSection.Payment);
            }
          }
        })
      );
    }

    if (this.quoteNotificationComp?.contactDetailsForm) {
      this.subscriptions.add(
        this.quoteNotificationComp.contactDetailsForm.valueChanges.subscribe(() => {
          if (this.sectionErrors.notifications && this.quoteNotificationComp?.isValid()) {
            this.clearSectionError(QuoteSection.Notifications);
          }
        })
      );
    }

    if (this.quoteOptionsComp?.quoteOptionsForm) {
      this.subscriptions.add(
        this.quoteOptionsComp.quoteOptionsForm.valueChanges.subscribe(() => {
          if (this.sectionErrors.options && this.quoteOptionsComp?.isCustomerTypeValid()) {
            this.clearSectionError(QuoteSection.Options);
          }
        })
      );
    }
  }

  /**
   * Handles navigation state from Barclays payment redirect.
   * Processes payment errors and order completion state passed via router state.
   */
  private handlePaymentRedirectState(): void {
    const state = window.history.state;

    if (state?.paymentError) {
      this.notificationService.showNotification(state.paymentError, NotificationType.Error);
    }

    // Check if order was completed after Barclays payment
    if (state?.orderCompleted) {
      this.orderIsCompleted = true;
      this.orderConfirmationNumber = state.orderConfirmationNumber;
      this.quoteId = state.orderId;
      this.quoteRevisionId = state.quoteRevisionId;
    }
  }

  /*
   * Removes the message with quote revision and makes the API call to remove revision if there are changes on the quote
   * @param shouldRemoveRevision - if true the isRevision attribute is removed from the quote
   */
  private resetQuoteRestored(shouldRemoveRevision?) {
    if (shouldRemoveRevision) {
      const quote = {
        quoteId: this.quoteDetailsComp.quoteId,
        quoteRevisionNumber: this.restoredQuote.quoteRevisionNumber
      };
      this.quoteDetailsService.removeRevision(quote)
        .subscribe(() => this.removeRestoredQuote());
    } else {
      this.removeRestoredQuote();
    }
  }

  private removeRestoredQuote() {
    this.quoteRestoredMessage = null;
    this.restoredQuote = null;
  }

  ngOnDestroy() {
    window.removeEventListener('beforeunload', this.unloadListener);
    if (window.location.pathname.includes('/quotes/')) {
      this.subscriptions.unsubscribe();
    } else {
      // delay unsubscribe to finish product summary update
      setTimeout(() => this.subscriptions.unsubscribe(), 1000);
    }
    this.routeSubscription.unsubscribe();
  }

  /**
   * Navigates to Quote Details section
   */
  goToQuoteDetails(event) {
    if (event) {
      event.stopPropagation();
    }

    if (this.currentPanel !== this.quoteDetailsPanel) {
      this.saveEnsNotifications();
      this.closePanelIfSwitching(this.quoteDetailsPanel);
      this.currentPanel = this.quoteDetailsPanel;
    }
    this.currentPanel.open();
  }

  /**
   * Validates Quote Details section
   */
  validateQuoteDetails() {

    // Bypass the check for form validation - AM control is disabled - the form will always be invalid
    if (this.electronAppOutOfSync || this.quoteIsCompleted) {
      this.stepsCompletion[QuoteStep.QuoteDetails] = true;
      return true;
    }

    const quoteDetailsForm = this.quoteDetailsComp.quoteDetailsForm;
    quoteDetailsForm.markAllAsTouched();

    if (quoteDetailsForm.valid) {
      this.stepsCompletion[QuoteStep.QuoteDetails] = true;
    } else {
      this.notificationService.showNotification(this.appStrings.products_gridRequiredFieldsError, NotificationType.Error);
    }

    return quoteDetailsForm.valid;
  }

  /**
   * Clears section error indicator when section becomes valid
   */
  private clearSectionError(section: QuoteSection) {
    this.sectionErrors[section] = false;
  }

  /**
   * Navigates to Product List section
   */
  goToProductList(event?) {
    if (event) {
      event.stopPropagation();
    }

    if (this.validateQuoteDetails()) {
      if (this.preventContinueIfNotBillingOrShipping()) {
        this.quoteProductsPanel.close();
        this.currentPanel.open();
        return;
      }

      if (this.currentPanel !== this.quoteProductsPanel) {
        this.saveEnsNotifications();
        this.closePanelIfSwitching(this.quoteProductsPanel);
        this.currentPanel = this.quoteProductsPanel;
        if (!this.quoteIsCompleted) {
          this.saveQuoteAndUpdateSummaries();
        } else {
          this.updateSummaries();
        }
      }
    } else {
      this.quoteProductsPanel.close();
    }

    this.currentPanel.open();
    if (this.currentPanel === this.quoteProductsPanel) {
      this.quoteProductsComp.focusPartNumberInput();
    }
  }

  /**
   * Display the expired quote notification if the quote is expired
   * @param quote - the expired quote
   */
  displayQuoteExpiredNotification(quote) {
    this.expiredQuote = quote;
    this.isExpiredQuote = true;
  }

  /**
   * Display the price matrix change quote notification if the quote's price matrix has changed after a merge
   * @param quote - the expired quote
   */
  displayPriceMatrixChangedAfterMergeNotification(quote) {
    if (quote) {
      if (quote.requiresPriceMatrixCurrencyChange) {
        this.priceMatrixChangedAfterMerge = true;
        this.quoteToUpdate = quote;
        this.updatingPriceMatrixAfterMergeButton = true;
      } else {
        this.priceMatrixChangedAfterMerge = false;
      }
    }
  }

  /**
   * Display the price matrix change quote notification if the quote's price matrix was changed by a user
   */
  displayNotificationBasedOnQuoteDetails() {
    this.priceMatrixChangedAfterMerge = false;
    this.updatingPriceMatrixAfterMergeButton = false;
  }

  /**
   * Handles a migrated quote
   * @param quote - the migrated quote
   */
  handleMigratedQuote(quote) {
    this.migratedQuote = quote;
    this.isMigratedQuote = true;
  }

  /**
   * Handles the click action on expired quote notification message
   */
  displayQuoteExpiredAction() {
    const expiredQuote = this.expiredQuote;
    const dialogRef = this.dialog.open(QuoteExpiredComponent, {
      width: this.isMobile ? '' : this.quoteExpiredDialogWidth,
      panelClass: this.deviceService.isMobile() ? ['ds', 'fullWidth'] : [],
      data: {
        expiredQuote
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.updatePricing) {
          this.updateQuotePricing(this.expiredQuote);
        } else {
          this.honorExpiredQuote(this.expiredQuote);
        }
      }
    });
  }

  /**
   * Handles the click action on migrated quote notification message
   */
  displayMigratedQuoteAction() {
    const confirmationText = `${this.appStrings.quote_migratedDialogMessage}${this.appStrings.quote_migratedDialogConfirmMessage}`;
    this.dialogTitle = this.appStrings.quote_migratedDialogTitle;
    this.dialogText = confirmationText;
    this.dialogButtons = [
      { label: this.appStrings.button_cancel, type: 'secondary' },
      { label: this.appStrings.button_confirm, type: 'primary', onclick: this.validateMigratedQuote.bind(this) },
    ];

    this.cdRef.detectChanges();
    this.dialogOpen$.emit(true);
  }

  displayUpdatedPriceMatrixesAndCurrencies() {
    this.priceMatrixChangedAfterMerge = false;
    this.updatingPriceMatrixAfterMergeButton = false;
    this.updateQuotePricing(this.quoteToUpdate);
    const id = this.quoteDetailsComp.quoteId;
    setTimeout(() => this.router.navigateByUrl('/', { skipLocationChange: true })
      .then(() => this.router.navigate(['/quotes', id])), 2000);
  }

  /**
   * Performs products validations
   * @param quote - the quote
   */
  private updateQuotePricing(quote) {

    this.isLoading = true;

    this.signalrService.startConnection(SignalrConnectionType.UpdatePricingOnExpiredQuote)
      .then(() => {
        const signalrNotification = new SignalrNotification(
          this.signalrService.connectionId,
          null,
          SignalrMethodName.updateQuotePricing
        );
        this.quoteDetailsService.updateQuotePricing(quote.id, signalrNotification)
          .subscribe(() => {
            // This is intentional
          }, () => this.isLoading = false);
      });
    this.shouldRefreshPage = true;

  }

  /**
   * Validate the products and recalculate the prices
   */
  private validateMigratedQuote() {
    this.updateQuotePricing(this.migratedQuote);
  }

  /**
   * Hides the notification and make the quote not to expire anymore
   * @param quote - the quote
   */
  private honorExpiredQuote(quote) {
    this.isLoading = true;
    this.quoteDetailsService.approveQuotePricing(quote.id)
      .subscribe(() => this.resetExpiredQuote());
  }

  /**
   * Removes expired quote notification
   */
  private resetExpiredQuote() {
    this.isExpiredQuote = false;
    this.expiredQuote = null;
    this.isLoading = false;
  }

  private resetMigratedQuote() {
    this.isMigratedQuote = false;
    this.migratedQuote = null;
    this.isLoading = false;
  }

  /**
   * Handles Product List section validation
   */
  validateProductList() {
    // add validation here
    this.stepsCompletion[QuoteStep.ProductList] = true;
    this.noProducts = !this.quoteProductsComp.totalRecords;
  }

  /**
   * Navigates to Payment Details section
   */
  goToPaymentDetails(event?) {
    if (event) {
      event.stopPropagation();
    }

    this.saveEnsNotifications();
    if (this.preventContinueIfNotBillingOrShipping()) {
      this.quotePaymentPanel.close();
      this.currentPanel.open();
      return;
    }

    if (this.currentPanel !== this.quotePaymentPanel) {
      this.validateQuoteDetails();
      this.validateProductList();

      if (this.stepsCompletion[QuoteStep.QuoteDetails] && this.stepsCompletion[QuoteStep.ProductList]) {
        this.closePanelIfSwitching(this.quotePaymentPanel);
        this.currentPanel = this.quotePaymentPanel;
        if (!this.quoteIsCompleted && !this.quotePaymentComp?.paymentDetailsOutdated) {
          this.saveQuoteAndUpdateSummaries();
        } else {
          this.updateSummaries();
        }
      } else {
        this.quotePaymentPanel.close();
      }
    }
    this.currentPanel.open();
  }

  saveQuoteAndUpdateSummaries() {
    this.saveQuote();
    this.updateSummaries();
    if (this.quotePaymentComp?.paymentDetailsForm?.valid && this.quotePaymentComp?.isPOAttachmentValid()) {
      this.clearSectionError(QuoteSection.Payment);
    }
  }

  saveQuoteAndUpdatePaymentDetails() {
    this.quotePaymentComp.paymentDetailsInProgress = true;
    this.saveQuote(() => {
      if (this.isLead) {
        const freight = this.quotePaymentComp.taxDetailsForm.controls.freightInput.value || 0;
        const taxRate = this.quotePaymentComp.taxDetailsForm.controls.taxRateInput.value || 0;
        this.paymentDetailsService.calculatePaymentSummary(this.quotePaymentComp.quoteId,
          () => {
            this.quotePaymentComp.paymentDetailsOutdated = false;
            this.quotePaymentComp.paymentDetailsInProgress = false;
          },
          {
            freight: freight.toString(),
            taxRate: taxRate.toString()
          }
        );
      } else {
        this.paymentDetailsService.calculatePaymentSummary(this.quotePaymentComp.quoteId,
          () => {
            this.quotePaymentComp.paymentDetailsOutdated = false;
            this.quotePaymentComp.paymentDetailsInProgress = false;
          });
      }
    });
  }

  /**
   * Updates summaries used for accordion
   */
  updateSummaries() {
    this.quoteDetailsService.summary.quoteDetails = this.quoteDetailsComp.getQuoteDetails();
  }

  /**
   * Validates confidence and estimated date fields for quotes >= 50,000
   * Ensures subtotal is updated before validation
   */
  private isConfidenceValid(): Promise<boolean> {
    return new Promise((resolve) => {
      const validateConfidence = () => {
        if (this.paymentSummary?.subTotal >= this.highValueQuoteLimit) {
          if (this.quoteDetailsComp?.quoteDetailsForm) {
            const quoteDetailsForm = this.quoteDetailsComp.quoteDetailsForm;
            const confidenceControl = quoteDetailsForm.controls.confidenceToClose;
            const estimatedDateControl = quoteDetailsForm.controls.estimatedCloseDate;

            confidenceControl.markAsTouched();
            estimatedDateControl.markAsTouched();

            const isValid = confidenceControl.valid && estimatedDateControl.valid;

            if (!isValid) {
              this.notificationService.showNotification(this.appStrings.quote_highValueValidationMessage, NotificationType.Error);
            }

            resolve(isValid);
          } else {
            resolve(false);
          }
        } else {
          resolve(true);
        }
      };

      if (this.quotePaymentComp.quoteId) {
        this.paymentDetailsService.calculatePaymentSummary(this.quotePaymentComp.quoteId, validateConfidence);
      } else {
        validateConfidence();
      }
    });
  }

  /**
   * Save quote (new or edit)
   */
  private saveQuote(actionAfterSave?: () => any) {
    if (this.quoteRestoredMessage) {
      if (this.quotePaymentComp.hasChanges() || this.quoteDetailsComp.quoteDetailsForm.dirty) {
        this.resetQuoteRestored(true);
      }
    }

    if (!this.quoteDetailsComp.hasChanges() && !this.quotePaymentComp.hasChanges()) {
      if (actionAfterSave) {
        setTimeout(() => actionAfterSave());
      }
      return;
    }

    const qDetails: QuoteDetails = this.quoteDetailsComp.getQuoteDetails();
    if (qDetails.id === this.appInfoService.emptyGuid) {
      this.createQuote(qDetails, actionAfterSave);
    } else {
      this.editQuoteWithSignalR(this.mapValuesForEditQuote(qDetails), actionAfterSave);
    }
  }

  /**
   * Creates a new quote when trying to save a quote with empty guid
   * @param qDetails quote details
   * @param actionAfterSave action to do after saving
   */
  createQuote(qDetails: QuoteDetails, actionAfterSave?: () => any) {
    this.quoteDetailsService.createQuote(qDetails,
      (quoteDetails: QuoteDetails) => {
        let cb = actionAfterSave;
        if (this.createdFromPriceAs && this.priceAsService.quoteInfo?.product) {
          // We have a quote created from the Price As page, add the product to the product list
          cb = () => {
            if (actionAfterSave) {
              actionAfterSave();
            }

            this.quoteToolsService.addPriceAsTool$.next(this.priceAsService.quoteInfo.product);
          };
        }
        this.callbackAfterQuoteAction(quoteDetails, cb);

        if (
          this.quoteNotificationComp?.loadedByCustomer &&
          this.quoteNotificationComp?.notifications?.length
        ) {
          this.quoteNotificationComp.save(quoteDetails.id);
        }
      });
  }

  private editQuoteWithSignalR(qDetails, actionAfterSave?: () => any) {
    if (actionAfterSave) {
      this.actionAfterQuoteSave = actionAfterSave;
    }

    this.quoteDetailsService.quoteIsSaving$.next(true);

    this.signalrService.startConnection(SignalrConnectionType.EditQuote, true)
      .then(() => {
        qDetails.notification = new SignalrNotification(
          null,
          this.appInfoService.userData.UserInfo.EmployeeId,
          SignalrMethodName.editQuote
        );
        this.quoteDetailsService.editQuote(qDetails,
          () => {
            // This is intentional
          }, () => {
            this.quoteDetailsService.quoteIsSaving$.next(false);
            if (this.quotePaymentComp) {
              this.quotePaymentComp.paymentDetailsInProgress = false;
            }
            if (this.actionAfterQuoteSave) {
              this.actionAfterQuoteSave = null;
            }
          }, true);
      });
  }

  private callbackAfterQuoteAction(quoteDetails: QuoteDetails, actionAfterSave) {
    this.quoteDetailsComp.updateQuoteDetailsAfterSave(quoteDetails);
    this.quotePaymentComp.updateQuoteId(quoteDetails.id);
    this.quoteNotificationComp?.setQuoteId(quoteDetails.id);
    this.updateSummaries();
    this.quoteDetailsService.quoteIsSaving$.next(false);

    this.savePaymentContactAndOptions(
      () => {
        if (actionAfterSave) {
          actionAfterSave();
          this.actionAfterQuoteSave = null;
        }
      },
      () => {
        this.notificationService.showNotification(
          this.appStrings.quote_saveErrorMessage || 'Error saving quote',
          NotificationType.Error
        );
      }
    );
  }

  private actionAfterEditQuoteDone(quoteId) {
    this.signalrService.closeConnection();
    if (this.saveAndCloseQuoteTriggered) { // ignore the quote get
      this.saveAndCloseQuoteTriggered = false;
      this.quoteDetailsComp.quoteDetailsForm.markAsPristine();

      this.savePaymentContactAndOptions(() => {
        if (this.actionAfterQuoteSave) {
          this.actionAfterQuoteSave();
          this.actionAfterQuoteSave = null;
        }
      });
    } else {
      this.quoteDetailsService.getQuoteDetails(quoteId,
        (quoteDetails: QuoteDetails) =>
          this.callbackAfterQuoteAction(quoteDetails, this.actionAfterQuoteSave));
    }
  }

  private mapValuesForEditQuote(details) {
    const values: any = {};

    if (this.isLead) {
      values.leadId = details.customer.id;
      values.customerId = null;
    } else {
      values.customerId = details.customer.id;
      values.leadId = null;
    }
    values.id = details.id;
    values.federalPriorityCode = details.federalPriorityCode;
    values.priceMatrix = details.priceMatrix;
    values.defaultRoute = details.defaultRoute;
    values.quoteNotes = details.quoteNotes;
    values.blockOrder = details.blockOrder;
    values.shipToComplete = details.shipToComplete;
    values.createdForEmployeeId = details.accountManager.employeeId;
    values.quoteName = details.quoteName;
    values.quoteExpireDate = details.quoteExpireDate;
    values.priceAsCustomerId = details.priceAs ? details.priceAs.id : null;
    values.atp = details.atp;
    values.confidenceToClose = Number(Object.keys(this.quoteDetailsComp.confidenceOptions).find(
      key => this.quoteDetailsComp.confidenceOptions[key] === details.confidenceToClose));
    values.estimatedCloseDate = details.estimatedCloseDate;
    if (values.confidenceToClose === 1) {
      values.estimatedCloseDate = details.quoteExpireDate;
    }
    return values;
  }

  /**
   * Calls save quote and closes the quote
   */
  saveAndCloseQuote() {
    if (this.quoteProductsComp?.startItemEditInProgress) {
      this.quoteProductsComp.saveEditedRow();
      return;
    }

    this.saveAndCloseQuoteTriggered = true;
    if (this.quoteDetailsComp.isValid()) {
      if (!this.toolEditInProgress && this.paymentSummaryUpdated) {
        const quoteOrPaymentHasChanges = this.quoteDetailsComp.hasChanges() || this.quotePaymentComp.hasChanges();
        this.saveQuote(() => {
          if (!quoteOrPaymentHasChanges && (this.quoteOptionsComp?.hasChanges() || this.quotePaymentComp.hasChanges())) {
            this.savePaymentContactAndOptions(() => this.saveEnsNotificationRows(() => this.closeQuote()));
          } else {
            this.saveContactOptionsAndNotifications(() => this.closeQuote());
          }
        });
      } else {
        this.notificationService.showNotification(this.appStrings.quoteEdit_pleaseWait, NotificationType.Warning);
      }
    }
  }

  closeQuote() {
    if (this.quoteProductsComp?.startItemEditInProgress) {
      this.quoteProductsComp.saveEditedRow();
      return;
    }

    this.router.navigate(['/quotes']);
  }

  /**
   * Confirms quote deletion
   */
  confirmDeleteQuote() {
    const deleteQuoteAction = () => {
      this.deleteQuote();
      this.called = true;
    };

    this.dialogTitle = this.appStrings.quote_detailsDeleteTitle;
    this.dialogText = this.appStrings.quote_detailsDeleteMessage;
    this.dialogButtons = [
      { label: 'Yes', type: 'primary', onclick: deleteQuoteAction.bind(this) },
      { label: 'No', type: 'secondary' },
    ];

    this.cdRef.detectChanges();
    this.dialogOpen$.emit(true);
  }


  /**
   * Deletes the quote
   */
  private deleteQuote() {
    const qDetails = this.quoteDetailsComp.getQuoteDetails();
    if (qDetails.id !== this.appInfoService.emptyGuid) {
      this.quoteDetailsService.deleteQuote(qDetails.id, () => {
        this.router.navigate(['/quotes']);
        this.called = true;
      });
    } else {
      this.router.navigate(['/quotes']);
    }
  }

  canDeactivate() {
    if (this.orderIsCompleted) { // allows to leave the page if the order is completed
      return true;
    } else {
      if (this.quoteProductsComp.startItemEditInProgress || this.doNotRefresh || this.quoteProductsComp.addProductInProgress) {
        return false;
      }
      return !(
        (this.quoteDetailsComp.hasChanges() && !this.called) ||
        this.quotePaymentComp.hasChanges() ||
        this.quoteOptionsComp?.hasChanges() ||
        this.quoteNotificationComp?.hasChanges()
      );
    }
  }

  private updateCustomer(newCustomer) {
    if (newCustomer.isLead) {
      this.lead = newCustomer;
      this.customer = null;
      this.isLead = true;
    } else {
      this.customer = newCustomer;
      this.lead = null;
      this.isLead = false;
    }
  }

  /**
   * Redirects to accounts-leads or to accounts based on customer type
   */
  goBack() {
    if (this.lead) {
      this.router.navigate(['/accounts-leads', this.lead.id]);
    } else {
      if (this.customer) {
        this.router.navigate(['/accounts', this.customer.id]);
      }
    }
  }

  /**
   * Pre-order completion checks
   */
  doOrderCompletion() {
    const isPaymentWithCC = this.quotePaymentComp.paymentDetailsForm.controls.creditCard.value;
    const pDetails = this.quotePaymentComp.getDetailsForCCPayment();
    if (isPaymentWithCC && this.isOnline) {
      // credit card is delayed to allow order details / tax to be stable in DB
      setTimeout(() => this.doCreditCardPayment(pDetails), this.creditCardStartDelayms);
    } else {
      this.completeOrder();
    }
  }

  private completeOrder() {
    this.quoteDetailsService.completeOrder(this.summary.quoteDetails.id).subscribe(order => {
      this.isLoading = false;
      this.doNotRefresh = false;
      this.orderEnsEmails = this.getEnsEmailsFromNotifications();
      this.orderContacts = this.quoteNotificationComp?.contactsList ?? [];
      this.orderIsCompleted = true;
      this.orderConfirmationNumber = order.quoteNumber;
      this.quoteId = order.quoteId;
      this.quoteRevisionId = order.quoteRevisionId;
      this.app.tick();
    }, () => {
      this.isLoading = false;
      this.app.tick();
    }
    );
  }


  /** Checks if the customer is an UK customer **/
  private isUKCustomer(customerCountryCodeId: number) {
    const foundCountry = this.quotePaymentComp.lnCountryList.find(country => country.value === countryCodeValues.GB);
    return foundCountry.key === customerCountryCodeId;
  }

  private doCreditCardPayment(paymentDetails) {
    if (this.isUKCustomer(this.quoteDetailsComp.currentCustomer.countryCodeId)) {
      paymentDetails.uid = this.appInfoService.userData.UserInfo.UserId;
      this.barclaysPaymentService.getSmartPayFuseStatus().subscribe(({ smartPayFuseActive }) => {
        this.doBarclaysPayment(smartPayFuseActive, paymentDetails);
      });
    } else {
      this.doChasePayment(paymentDetails);
    }
  }

  private doChasePayment(paymentDetails) {
    let uid;
    const quoteId = paymentDetails.quoteId;
    this.paymentDetailsService.getTokenForChase(quoteId, paymentDetails.billingAddress)
      .subscribe((result: ArrayBuffer) => {
        uid = String.fromCharCode.apply(null, new Uint8Array(result));
        this.doNotRefresh = true;
        this.paymentDetailsService.startCreditCardPayment(
          quoteId,
          uid,
          () => this.completeOrder(),
          () => this.creditCardCancelled(),
          () => this.creditCardCancelled()
        );
      });
  }

  private doBarclaysPayment(smartPayFuseActive: boolean, paymentDetails?) {
    if (smartPayFuseActive) {
      this.router.navigate(['/quotes', this.summary.quoteDetails.id, 'payment']);
    } else {
      this.doLegacyBarclaysPayment(paymentDetails);
    }
  }

  private doLegacyBarclaysPayment(paymentDetails) {
    this.barclaysPaymentService.sendBarclaysCreditCardPaymentInfo(paymentDetails)
      .subscribe((barclaysResponse: BarclaysRequest) => {
        this.doNotRefresh = true;
        const dialogParams: MatDialogConfig = {
          closeOnNavigation: true,
          data: { BarclaysRequest: barclaysResponse },
          disableClose: true,
          autoFocus: false,
          maxHeight: '700px',
          maxWidth: '90%',
          width: '1000px'
        };
        const dialogRef = this.dialog.open(PaymentBarclaysLegacyComponent, dialogParams);
        dialogRef.afterClosed().subscribe((barclaysRes: BarclaysResponse) => this.processBarclaysResponse(barclaysRes));
      });
  }

  private processBarclaysResponse(barclaysResponseFromModal: BarclaysResponse) {
    this.barclaysPaymentService.sendBarclaysPaymentResponse(barclaysResponseFromModal)
      .subscribe((barclaysPaymentStatus: number) => this.handleBarclaysPaymentStatus(barclaysPaymentStatus));
  }

  private handleBarclaysPaymentStatus(status: number) {
    if (status === PaymentStatus.Completed) {
      this.completeOrder();
      return;
    }
    this.creditCardCancelled();
    if (status === PaymentStatus.Canceled) {
      return;
    }
    const notificationMessage = status === PaymentStatus.Rejected ?
      this.appStrings.payment_barclaysRejectedPayment : this.appStrings.payment_barclaysPaymentException;
    this.notificationService.showNotification(notificationMessage, NotificationType.Error);
  }

  private creditCardCancelled() {
    this.isLoading = false;
    this.doNotRefresh = false;
    this.app.tick();
  }

  /**
   * Completes the quote
   * Currently there's no request to validate the data before completing the quote
   */
  completeQuote() {
    this.isLoading = true;
    this.quotePaymentComp.triggeredFieldValidations = true;

    this.isConfidenceValid().then(isValid => {
      const notificationsInvalid = this.quoteNotificationComp && !this.quoteNotificationComp.areEmailRowsValid();

      if (!isValid || notificationsInvalid) {
        if (notificationsInvalid) {
          this.sectionErrors.notifications = true;
          this.notificationService.showNotification(this.appStrings.order_completeErrorsMessage, NotificationType.Error);
        } else {
          this.clearSectionError(QuoteSection.Notifications);
        }
        if (!isValid) {
          this.sectionErrors.payment = true;
        }
        this.isLoading = false;
        return;
      }

      this.clearSectionError(QuoteSection.Notifications);

      if (!this.quotePaymentComp.validatePayment()) {
        this.quotePaymentPanelDesktop.open();
        this.isLoading = false;
        return;
      }

      if (!this.paymentSummaryUpdated && this.productListHasChanges) {
        this.notificationService.showNotification(this.appStrings.quoteEdit_pleaseWait, NotificationType.Warning);
        this.isLoading = false;
        return;
      }

      if (this.preventContinueIfNotBillingOrShipping()) {
        this.isLoading = false;
        return;
      }

      if (this.quoteOptionsComp && !this.quoteOptionsComp.isCustomerTypeValid()) {
        this.sectionErrors.options = true;
        this.notificationService.showNotification(this.appStrings.order_completeErrorsMessage, NotificationType.Error);
        this.isLoading = false;
        return;
      }

      this.clearSectionError(QuoteSection.Options);
      this.saveAndCompleteQuote();
    });
  }

  private saveAndCompleteQuote() {
    this.saveQuote(() => {
      this.saveContactOptionsAndNotifications(() => {
        this.quoteDetailsService.completeQuote(this.quotePaymentComp.quoteId)
          .subscribe(result => this.processAfterQuoteCompletion(result));
      });
    });
  }

  seeOrderAcknowledgment() {
    this.quoteDetailsService.getOrderRevision(this.quotePaymentComp.quoteId).subscribe(result => this.openQuoteCompletionPop(result, true));
  }

  /**
   * Initiates order completion
   */
  initiateCompleteOrder() {
    const isPaymentWithCC = this.quotePaymentComp.paymentDetailsForm.controls.creditCard.value;
    this.isLoading = true;
    this.quotePaymentComp.triggeredFieldValidations = true;

    if (!this.validateOrderSections() || !this.quotePaymentComp.validatePayment()) {
      this.isLoading = false;
      return;
    }

    this.saveQuote(() => {
      if (!this.isOnline && isPaymentWithCC) {
        this.notificationService.showNotification(this.appStrings.offline_tryCreditCardPaymentErrorMessage, NotificationType.Error);
        this.isLoading = false;
        return;
      }

      if (this.quotePaymentComp.hasChanges()) {
        this.savePaymentDetails();
      } else {
        this.saveRemainingAndValidateOrder();
      }
    });
  }

  private validateOrderSections(): boolean {
    const productsInvalid = !!this.errorCount;
    const paymentInvalid = !this.quotePaymentComp.isPaymentValid();
    const notificationsInvalid = this.quoteNotificationComp && !this.quoteNotificationComp.isValid();
    const customerTypeMissing = this.quoteOptionsComp && !this.quoteOptionsComp.isCustomerTypeValid();

    this.setSectionError(QuoteSection.Products, productsInvalid);
    this.setSectionError(QuoteSection.Payment, paymentInvalid);
    this.setSectionError(QuoteSection.Notifications, notificationsInvalid);
    this.setSectionError(QuoteSection.Options, customerTypeMissing);

    if (notificationsInvalid) {
      this.quoteNotificationComp.markAllAsTouched();
    }

    if (productsInvalid) {
      this.showQuoteErrors();
      return false;
    }

    if (paymentInvalid) {
      return false;
    }

    if (notificationsInvalid || customerTypeMissing) {
      this.notificationService.showNotification(this.appStrings.order_completeErrorsMessage, NotificationType.Error);
      return false;
    }

    return true;
  }

  private setSectionError(section: QuoteSection, hasError: boolean) {
    if (hasError) {
      this.sectionErrors[section] = true;
    } else {
      this.clearSectionError(section);
    }
  }

  /**
   * Checking if quote notification has changes and validating payment and order
   */
  private saveRemainingAndValidateOrder() {
    this.saveContactOptionsAndNotifications(() => {
      this.checkIfPaymentIsValidAndValidateOrder();
    });
  }

  /**
   * Showing notification to complete errors and update errors flags
   */
  private showQuoteErrors() {
    this.notificationService.showNotification(this.appStrings.order_completeErrorsMessage, NotificationType.Error, true);
    this.quoteProductsComp.warnForErrors = true;
    this.warnForErrors = true;
    this.isLoading = false;
  }

  private savePaymentDetails() {
    this.isLoading = true;
    const contact = this.quoteNotificationComp?.getContactDetails();
    const quoteAttributes = this.quoteOptionsComp?.getQuoteAttributes();
    const pDetails = this.quotePaymentComp.getPaymentDetails(
      this.quotePaymentComp.paymentDetailsForm.controls.creditCard.value,
      contact,
      quoteAttributes
    );

    this.paymentDetailsService.savePaymentDetails(pDetails).subscribe((paymentDetails) => {
      this.isLoading = false;
      this.paymentDetailsResponse = paymentDetails;
      this.quoteOptionsComp?.markAsPristine();
      this.quoteNotificationComp?.contactDetailsForm?.markAsPristine();
      this.saveEnsNotificationRows(() => {
        this.checkIfPaymentIsValidAndValidateOrder();
      });
    }, () => {
      this.isLoading = false;
      this.notificationService.showNotification(this.appStrings.general_SaveFailed, NotificationType.Error, true);
    });
  }

  private checkIfPaymentIsValidAndValidateOrder() {
    if (this.quotePaymentComp.isPaymentValid()) {
      this.notificationService.dismissNotification();
      this.isLoading = true;
      this.orderValidationInProgress = true;
      this.validateOrderWithSignalR();
    } else {
      this.isLoading = false;
      if (this.paymentDetailsResponse.id) {
        this.quotePaymentComp.updatePaymentDetailsAfterSave(this.paymentDetailsResponse);
      }
    }
  }

  private actionAfterOrderValidation() {
    this.signalrService.closeConnection();
    this.quoteToolsService.getProductList(0, null, null, () => this.doOrderCompletion());
  }

  /**
   * Method used to test signal R implementation
   */
  private validateOrderWithSignalR() {
    this.signalrService.startConnection(SignalrConnectionType.ValidateOrder)
      .then(() => {
        const signalrNotification = new SignalrNotification(
          this.signalrService.connectionId,
          null,
          SignalrMethodName.validate
        );
        const validateOrderReqObj = {
          quoteId: this.summary.quoteDetails.id,
          notification: signalrNotification
        };
        this.quoteDetailsService.validateOrder(validateOrderReqObj)
          .subscribe(
            () => this.orderValidationInProgress = false,
            () => {
              this.isLoading = false;
              this.orderValidationInProgress = false;
            });
      })
      .catch((err: string) => {
        this.orderValidationInProgress = false;
        this.notificationService.showNotification(err, NotificationType.Error);
      });
  }

  /**
   * After the quote is completed action
   * @param details - the response from quote completion
   */
  private processAfterQuoteCompletion(details) {
    if (this.quoteVersioningComp) {
      this.quoteVersioningComp.quoteId = details.quoteId;
      this.quoteVersioningComp.getQuoteRevisions();
    }
    this.openQuoteCompletionPop(details);
    this.isLoading = false;
  }

  /**
   * Opens the dialog to choose action after quote completion
   * @param as details
   */
  private openQuoteCompletionPop(details, isOrder?) {
    const ensEmails = this.getEnsEmailsFromNotifications();
    const contacts = this.quoteNotificationComp?.contactsList ?? [];

    const dialogRef = this.dialog.open(PrintExportQuoteComponent, {
      width: this.quoteCompletionDialogWidth,
      data: {
        details,
        isOrder,
        ensEmails,
        contacts
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.shouldPrintQuote) {
          this.printQuote(details);
        } else if (result.shouldExportQuote) {
          this.exportQuote(details);
        } else if (result.shouldEmailQuote) {
          this.emailQuote(result.emailAddresses, details.quoteRevisionId);
        }
      }
    });
  }

  /**
   * Prints the quote
   */
  private printQuote(details) {
    this.quoteToolsService.printQuote(details, true);
  }

  /**
   * Exports the quote
   */
  private exportQuote(details) {
    this.quoteToolsService.exportQuote(details, true);
  }

  /**
   * Emails the quote to the given addresses
   * @param emailAddresses - array of email addresses to send the quote to
   * @param revisionId - the quote revision id
   */
  private emailQuote(emailAddresses: string[], revisionId: string) {
    this.quoteToolsService.emailQuote(revisionId, emailAddresses)
      .subscribe((result: BaseResponse) => {
        this.notificationService.showNotification(result.actionMessage, NotificationType.Success);
      });
  }

  /**
   * Handles display of the signalR order validation
   * @param as orderValidateProgress
   */
  private setOrderValidateProgress(orderValidateProgress: SignalRUpload) {
    if (orderValidateProgress.state === SignalrProgressState.Done) {
      this.actionAfterOrderValidation();
    }
    if (orderValidateProgress.state === SignalrProgressState.Error) {
      this.isLoading = false;
      this.orderValidationInProgress = false;
      this.signalrService.closeConnection();
      this.notificationService.showNotification(orderValidateProgress.message, NotificationType.Error);
    }
  }

  private setUpdatePricingProgress(updatePricingProgress: SignalRUpload) {
    if (updatePricingProgress.state === SignalrProgressState.Done) {
      this.signalrService.closeConnection();
      this.quoteToolsService.getProductList(0, null, null,
        () => {
          this.resetExpiredQuote();
          this.resetMigratedQuote();
          this.paymentDetailsService.calculatePaymentSummary(this.quoteDetailsComp.quoteId, () => {
            this.isLoading = false;
          });
          if (this.shouldRefreshPage) {
            if (this.appStateService.state.isElectron) {
              this.quoteToolsService.productListHasChanges$.next(false);
              this.router.navigate(['/quotes', this.quoteId, { nonce: Math.random() }]);
              return;
            }
            window.location.reload();

          }
        });
    }
    if (updatePricingProgress.state === SignalrProgressState.Error) {
      this.afterUpdatePricingAction();
      this.notificationService.showNotification(updatePricingProgress.message, NotificationType.Error);
    }
  }

  private afterUpdatePricingAction() {
    this.isLoading = false;
    this.signalrService.closeConnection();
  }

  private setEditQuoteProgress(progress) {
    this.quoteDetailsService.quoteIsSaving$.next(false);
    if (progress.state === SignalrProgressState.Done) {
      this.actionAfterEditQuoteDone(progress.message.entityId);
    }

    if (progress.state === SignalrProgressState.Error) {
      this.signalrService.closeConnection();
      this.notificationService.showNotification(progress.message, NotificationType.Error, true);
      if (this.actionAfterQuoteSave) {
        this.actionAfterQuoteSave = null;
      }
    }
  }

  /**
   * Prevents the user to continue the desired flow if billing addresses list is empty  or shipping address doesn't exist
   */
  private preventContinueIfNotBillingOrShipping() {
    if (!this.isMigratedQuote && !this.isLead &&
      (!this.paymentDetailsService.callsDone.billingAddresses || !this.paymentDetailsService.callsDone.shippingAddresses)) {
      this.notificationService.showNotification(this.appStrings.quote_completeNoShippingOrBilling, NotificationType.Warning);
      return true;
    }
  }

  /**
   * Determines tax naming based on country code
   * @param countryCode country code
   */
  determineTaxNaming(countryCode) {
    if (countriesFromEurope[countryCode]) {
      this.taxNaming = this.appStrings.paymentDetails_european_summary_tax;
    } else {
      if (countryCode === 'AU') {
        this.taxNaming = this.appStrings.paymentDetails_australia_summary_tax;
      } else {
        this.taxNaming = this.appStrings.paymentDetails_summary_tax;
      }
    }
  }

  /**
   * Closes the current panel if on mobile and switching to a different panel
   * @param targetPanel - The panel we're switching to
   */
  private closePanelIfSwitching(targetPanel: MatExpansionPanel) {
    if (this.currentPanel && this.currentPanel !== targetPanel) {
      this.currentPanel.close();
    }
  }

  /**
   * Opens the Quote Options panel
   */
  goToQuoteOptions(event?) {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }

    if (this.preventContinueIfNotBillingOrShipping()) {
      this.quoteOptionsPanel.close();
      this.currentPanel.open();
      return;
    }

    if (this.currentPanel !== this.quoteOptionsPanel) {
      this.saveEnsNotifications();
      this.validateQuoteDetails();
      this.validateProductList();

      if (this.stepsCompletion[QuoteStep.QuoteDetails] && this.stepsCompletion[QuoteStep.ProductList]) {
        this.closePanelIfSwitching(this.quoteOptionsPanel);
        this.currentPanel = this.quoteOptionsPanel;
        this.currentPanel.open();
      }
    } else {
      this.currentPanel.open();
    }
  }

  /**
   * Opens the Order Email Notification panel
   */
  goToOrderNotifications($event) {
    if ($event) {
      $event.stopPropagation();
    }

    this.saveEnsNotifications();
    if (this.validateQuoteDetails()) {
      if (this.preventContinueIfNotBillingOrShipping()) {
        this.quoteNotificationPanel.close();
        this.currentPanel.open();
        return;
      }

      this.closePanelIfSwitching(this.quoteNotificationPanel);
      this.currentPanel = this.quoteNotificationPanel;
      if (!this.quoteIsCompleted) {
        this.saveQuoteAndUpdateSummaries();
      } else {
        this.updateSummaries();
      }
    } else {
      this.quoteNotificationPanel.close();
    }

    this.currentPanel.open();
  }

  goToQuoteHistory($event) {
    if ($event) {
      $event.stopPropagation();
    }

    this.saveEnsNotifications();
    if (this.validateQuoteDetails()) {
      if (this.preventContinueIfNotBillingOrShipping()) {
        this.quoteHistoryPanel.close();
        this.currentPanel.open();
        return;
      }

      this.closePanelIfSwitching(this.quoteHistoryPanel);
      this.currentPanel = this.quoteHistoryPanel;
      if (!this.quoteIsCompleted) {
        this.saveQuoteAndUpdateSummaries();
      } else {
        this.updateSummaries();
      }
    } else {
      this.quoteHistoryPanel.close();
    }

    this.currentPanel.open();
  }

  /**
   * Saves contact, options, and ENS notification rows with no follow-up action.
   */
  private saveEnsNotifications() {
    this.saveContactOptionsAndNotifications(() => { });
  }

  /**
   * Builds payment details from sub-components, saves them, updates the cache,
   * and then invokes the success callback. Used in both quote-save flows.
   * TODO refactor to use separate API endpoints
   */
  private savePaymentContactAndOptions(onSuccess: () => void, onError?: () => void) {
    const contact = this.quoteNotificationComp?.getContactDetails();
    const quoteAttributes = this.quoteOptionsComp?.getQuoteAttributes();
    const pDetails = this.quotePaymentComp.getPaymentDetails(false, contact, quoteAttributes);

    this.paymentDetailsService.savePaymentDetails(pDetails).subscribe(
      (paymentDetails) => {
        this.quotePaymentComp.updatePaymentDetailsAfterSave(paymentDetails);
        if (this.quoteOptionsComp?.hasChanges()) {
          this.paymentDetailsService.saveQuoteAttributes(quoteAttributes);
        }
        this.quoteOptionsComp?.markAsPristine();
        this.quoteNotificationComp?.contactDetailsForm?.markAsPristine();
        onSuccess();
      },
      () => {
        this.isLoading = false;
        onError?.();
      }
    );
  }

  /**
   * Saves contact details and quote options (via savePaymentContactAndOptions) if either is dirty,
   * then saves ENS notification rows, then invokes the callback.
   */
  private saveContactOptionsAndNotifications(callback: () => void) {
    if (this.quoteNotificationComp?.contactDetailsForm?.dirty || this.quoteOptionsComp?.hasChanges()) {
      this.savePaymentContactAndOptions(() => this.saveEnsNotificationRows(callback));
    } else {
      this.saveEnsNotificationRows(callback);
    }
  }

  private saveEnsNotificationRows(actionAfterSave: () => void) {
    if (!this.isLead && this.quoteNotificationComp?.hasChanges()) {
      this.quoteDetailsService.quoteIsSaving$.next(true);
      this.quoteNotificationComp.save(null, () => {
        this.quoteDetailsService.quoteIsSaving$.next(false);
        actionAfterSave();
      });
    } else {
      actionAfterSave();
    }
  }

  private getEnsEmailsFromNotifications(): string[] {
    return this.quoteNotificationComp?.notifications?.getRawValue()
      ?.slice(1)
      ?.map((n: any) => n.email)
      ?.filter((e: string) => !!e) ?? [];
  }
}
